import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const STATUS_STYLES = {
  completed:   'bg-green-100 text-green-700',
  failed:      'bg-red-100 text-red-700',
  in_progress: 'bg-blue-100 text-blue-700',
  pending:     'bg-gray-100 text-gray-500',
};

const TYPE_LABELS = {
  manual:        'Manual',
  scheduled:     'Scheduled',
  pre_migration: 'Pre-Migration',
  pre_delete:    'Pre-Delete',
};

function formatBytes(bytes) {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

function formatDuration(ms) {
  if (!ms) return '—';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

export default function Backups() {
  const [backups, setBackups] = useState([]);
  const [stats, setStats] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ tenant_code: '', status: '', type: '' });
  const [page, setPage] = useState(0);
  const [busyAction, setBusyAction] = useState(null); // 'tenant:CODE', 'master', 'all', 'restore:ID', 'delete:ID'
  const [toast, setToast] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null); // { action, title, message, onConfirm }
  const [selectedTenant, setSelectedTenant] = useState('');
  const [backupScope, setBackupScope] = useState('full');
  const LIMIT = 25;

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchBackups = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filter.tenant_code) params.set('tenant_code', filter.tenant_code);
      if (filter.status) params.set('status', filter.status);
      if (filter.type) params.set('type', filter.type);
      params.set('limit', LIMIT);
      params.set('offset', page * LIMIT);
      const { data } = await api.get(`/backups?${params}`);
      setBackups(data.data.backups || []);
      setTotal(data.data.total || 0);
    } catch { setBackups([]); }
  }, [filter, page]);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await api.get('/backups/stats');
      setStats(data.data);
    } catch {}
  }, []);

  const fetchTenants = useCallback(async () => {
    try {
      const { data } = await api.get('/tenants?limit=200');
      setTenants(data.data || []);
    } catch {}
  }, []);

  useEffect(() => {
    Promise.all([fetchBackups(), fetchStats(), fetchTenants()]).finally(() => setLoading(false));
  }, [fetchBackups, fetchStats, fetchTenants]);

  // ─── Actions ────────────────────────────────────────────────────
  const handleBackupTenant = async () => {
    if (!selectedTenant) return;
    setBusyAction(`tenant:${selectedTenant}`);
    try {
      const { data } = await api.post(`/backups/tenant/${selectedTenant}`, { scope: backupScope });
      showToast(data.message);
      fetchBackups(); fetchStats();
    } catch (e) {
      showToast(e.response?.data?.message || 'Backup failed', 'error');
    } finally { setBusyAction(null); }
  };

  const handleBackupMaster = async () => {
    setBusyAction('master');
    try {
      const { data } = await api.post('/backups/master', { scope: backupScope });
      showToast(data.message);
      fetchBackups(); fetchStats();
    } catch (e) {
      showToast(e.response?.data?.message || 'Backup failed', 'error');
    } finally { setBusyAction(null); }
  };

  const handleBackupAll = async () => {
    setBusyAction('all');
    try {
      const { data } = await api.post('/backups/all', { scope: backupScope });
      showToast(data.message);
      fetchBackups(); fetchStats();
    } catch (e) {
      showToast(e.response?.data?.message || 'Backup failed', 'error');
    } finally { setBusyAction(null); }
  };

  const handleDownload = (id, fileName) => {
    // Use axios instance for proper auth token refresh support
    api.get(`/backups/${id}/download`, { responseType: 'blob' })
      .then(res => {
        const url = URL.createObjectURL(res.data);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        link.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => showToast('Download failed', 'error'));
  };

  const handleRestore = async (id, dbName) => {
    setConfirmModal({
      action: 'restore',
      title: 'Restore Database',
      message: `This will overwrite the current "${dbName}" database with this backup. All current data will be replaced. Continue?`,
      color: 'amber',
      onConfirm: async () => {
        setConfirmModal(null);
        setBusyAction(`restore:${id}`);
        try {
          const { data } = await api.post(`/backups/${id}/restore`);
          showToast(data.message);
        } catch (e) {
          showToast(e.response?.data?.message || 'Restore failed', 'error');
        } finally { setBusyAction(null); }
      },
    });
  };

  const handleDelete = async (id) => {
    setConfirmModal({
      action: 'delete',
      title: 'Delete Backup',
      message: 'Delete this backup file permanently? This cannot be undone.',
      color: 'red',
      onConfirm: async () => {
        setConfirmModal(null);
        setBusyAction(`delete:${id}`);
        try {
          await api.delete(`/backups/${id}`);
          showToast('Backup deleted');
          fetchBackups(); fetchStats();
        } catch (e) {
          showToast(e.response?.data?.message || 'Delete failed', 'error');
        } finally { setBusyAction(null); }
      },
    });
  };

  const handleCleanup = async () => {
    setConfirmModal({
      action: 'cleanup',
      title: 'Cleanup Expired Backups',
      message: 'Remove all expired backup files based on the retention policy?',
      color: 'amber',
      onConfirm: async () => {
        setConfirmModal(null);
        setBusyAction('cleanup');
        try {
          const { data } = await api.post('/backups/cleanup');
          showToast(data.message);
          fetchBackups(); fetchStats();
        } catch (e) {
          showToast(e.response?.data?.message || 'Cleanup failed', 'error');
        } finally { setBusyAction(null); }
      },
    });
  };

  const totalPages = Math.ceil(total / LIMIT);

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Database Backups</h1>
          <p className="text-sm text-gray-500 mt-1">Create, manage, and restore database backups for all tenants</p>
        </div>
        <button onClick={handleCleanup} disabled={!!busyAction} className="px-3 py-2 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
          🧹 Cleanup Expired
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            { label: 'Total Backups', value: stats.total_backups, color: 'blue' },
            { label: 'Completed', value: stats.completed, color: 'green' },
            { label: 'Failed', value: stats.failed, color: 'red' },
            { label: 'Total Size', value: formatBytes(stats.total_size_bytes), color: 'purple' },
            { label: 'Last Backup', value: stats.last_backup_at ? new Date(stats.last_backup_at).toLocaleDateString() : 'Never', color: 'amber' },
          ].map(({ label, value, color }) => (
            <div key={label} className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
              <div className="text-xs text-gray-500 font-medium">{label}</div>
              <div className={`text-xl font-bold mt-1 text-${color}-600`}>{value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Backup Actions */}
      <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
        <h3 className="text-sm font-semibold text-gray-900 mb-4">Create Backup</h3>
        <div className="flex flex-wrap items-end gap-4">
          {/* Backup Scope */}
          <div className="min-w-[180px]">
            <label className="block text-xs text-gray-500 mb-1.5">Backup Scope</label>
            <select
              value={backupScope}
              onChange={e => setBackupScope(e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="full">Full (Structure + Data)</option>
              <option value="data_only">Data Only</option>
            </select>
          </div>
          {/* Tenant Backup */}
          <div className="flex-1 min-w-[250px]">
            <label className="block text-xs text-gray-500 mb-1.5">Backup Tenant Database</label>
            <div className="flex gap-2">
              <select
                value={selectedTenant}
                onChange={e => setSelectedTenant(e.target.value)}
                className="flex-1 px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select tenant...</option>
                {tenants.filter(t => t.status !== 'deleted').map(t => (
                  <option key={t.code} value={t.code}>{t.name} ({t.code})</option>
                ))}
              </select>
              <button
                onClick={handleBackupTenant}
                disabled={!selectedTenant || !!busyAction}
                className="px-4 py-2.5 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2 whitespace-nowrap"
              >
                {busyAction?.startsWith('tenant:') && <Spinner />}
                Backup Tenant
              </button>
            </div>
          </div>
          {/* Master Backup */}
          <button
            onClick={handleBackupMaster}
            disabled={!!busyAction}
            className="px-4 py-2.5 text-sm text-white bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {busyAction === 'master' && <Spinner />}
            Backup Master DB
          </button>
          {/* Backup All */}
          <button
            onClick={() => setConfirmModal({
              action: 'backupAll',
              title: 'Backup All Databases',
              message: `This will backup the master database and all ${tenants.filter(t => t.status !== 'deleted').length} active tenant databases. This may take several minutes.`,
              color: 'blue',
              onConfirm: () => { setConfirmModal(null); handleBackupAll(); },
            })}
            disabled={!!busyAction}
            className="px-4 py-2.5 text-sm text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {busyAction === 'all' && <Spinner />}
            Backup All
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm font-medium text-gray-700">Filter:</span>
          <select
            value={filter.tenant_code}
            onChange={e => { setFilter(f => ({ ...f, tenant_code: e.target.value })); setPage(0); }}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
          >
            <option value="">All Databases</option>
            <option value="master">Master DB</option>
            {tenants.map(t => (
              <option key={t.code} value={t.code}>{t.name} ({t.code})</option>
            ))}
          </select>
          <select
            value={filter.status}
            onChange={e => { setFilter(f => ({ ...f, status: e.target.value })); setPage(0); }}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
          >
            <option value="">All Status</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
            <option value="in_progress">In Progress</option>
          </select>
          <select
            value={filter.type}
            onChange={e => { setFilter(f => ({ ...f, type: e.target.value })); setPage(0); }}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
          >
            <option value="">All Types</option>
            <option value="manual">Manual</option>
            <option value="scheduled">Scheduled</option>
            <option value="pre_migration">Pre-Migration</option>
            <option value="pre_delete">Pre-Delete</option>
          </select>
          {(filter.tenant_code || filter.status || filter.type) && (
            <button onClick={() => { setFilter({ tenant_code: '', status: '', type: '' }); setPage(0); }} className="text-xs text-blue-600 hover:text-blue-700">
              Clear Filters
            </button>
          )}
          <span className="ml-auto text-xs text-gray-400">{total} backup{total !== 1 ? 's' : ''}</span>
        </div>
      </div>

      {/* Backup List */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        {backups.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
            </svg>
            <p className="text-sm">No backups found</p>
            <p className="text-xs mt-1">Create your first backup using the options above</p>
          </div>
        ) : (
          <>
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b text-xs font-semibold text-gray-500 uppercase tracking-wider">
                <th className="px-4 py-3 text-left">Database</th>
                <th className="px-4 py-3 text-left">Type</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left">Size</th>
                <th className="px-4 py-3 text-left">Objects</th>
                <th className="px-4 py-3 text-left">Duration</th>
                <th className="px-4 py-3 text-left">Created</th>
                <th className="px-4 py-3 text-left">By</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {backups.map(b => (
                <tr key={b.id} className="hover:bg-gray-50/50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="text-sm font-medium text-gray-900">{b.db_name}</div>
                    <div className="text-xs text-gray-400">
                      {b.tenant_code ? `${b.tenant_name || b.tenant_code}` : 'Master DB'}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">
                      {TYPE_LABELS[b.backup_type] || b.backup_type}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_STYLES[b.status] || 'bg-gray-100 text-gray-500'}`}>
                      {b.status === 'in_progress' ? 'Running' : b.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 font-mono">
                    {b.status === 'completed' ? formatBytes(b.file_size_bytes) : '—'}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">
                    {b.status === 'completed' ? `${b.tables_count}T / ${b.procedures_count}SP` : '—'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">{formatDuration(b.duration_ms)}</td>
                  <td className="px-4 py-3">
                    <div className="text-xs text-gray-700">{new Date(b.created_at).toLocaleDateString()}</div>
                    <div className="text-xs text-gray-400">{new Date(b.created_at).toLocaleTimeString()}</div>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">{b.initiated_by || '—'}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end items-center gap-1">
                      {b.status === 'completed' && (
                        <>
                          <button
                            onClick={() => handleDownload(b.id, b.file_name)}
                            title="Download"
                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                          </button>
                          <button
                            onClick={() => handleRestore(b.id, b.db_name)}
                            disabled={!!busyAction}
                            title="Restore"
                            className="p-1.5 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors disabled:opacity-50"
                          >
                            {busyAction === `restore:${b.id}` ? <Spinner small /> : (
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                            )}
                          </button>
                        </>
                      )}
                      {b.status === 'failed' && b.error_message && (
                        <button
                          onClick={() => setConfirmModal({
                            action: 'info',
                            title: 'Error Details',
                            message: b.error_message,
                            color: 'red',
                            onConfirm: () => setConfirmModal(null),
                            confirmLabel: 'OK',
                          })}
                          title="View Error"
                          className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(b.id)}
                        disabled={!!busyAction}
                        title="Delete"
                        className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                      >
                        {busyAction === `delete:${b.id}` ? <Spinner small /> : (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
              <span className="text-xs text-gray-500">
                Showing {page * LIMIT + 1}–{Math.min((page + 1) * LIMIT, total)} of {total}
              </span>
              <div className="flex gap-1">
                <button onClick={() => setPage(p => Math.max(p - 1, 0))} disabled={page === 0} className="px-3 py-1.5 text-xs border rounded-lg disabled:opacity-40">Prev</button>
                <button onClick={() => setPage(p => Math.min(p + 1, totalPages - 1))} disabled={page >= totalPages - 1} className="px-3 py-1.5 text-xs border rounded-lg disabled:opacity-40">Next</button>
              </div>
            </div>
          )}
          </>
        )}
      </div>

      {/* Confirm Modal */}
      {confirmModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setConfirmModal(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center space-x-3 mb-4">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                confirmModal.color === 'red' ? 'bg-red-100' : confirmModal.color === 'amber' ? 'bg-amber-100' : 'bg-blue-100'
              }`}>
                <svg className={`w-5 h-5 ${
                  confirmModal.color === 'red' ? 'text-red-600' : confirmModal.color === 'amber' ? 'text-amber-600' : 'text-blue-600'
                }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-gray-900">{confirmModal.title}</h3>
            </div>
            <p className="text-sm text-gray-600 mb-6 whitespace-pre-wrap">{confirmModal.message}</p>
            <div className="flex justify-end space-x-3">
              <button onClick={() => setConfirmModal(null)} className="px-4 py-2 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">Cancel</button>
              <button
                onClick={confirmModal.onConfirm}
                className={`px-4 py-2 text-sm text-white rounded-lg transition-colors ${
                  confirmModal.color === 'red' ? 'bg-red-600 hover:bg-red-700' :
                  confirmModal.color === 'amber' ? 'bg-amber-600 hover:bg-amber-700' :
                  'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {confirmModal.confirmLabel || 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 right-6 z-50 px-5 py-3 rounded-xl shadow-lg text-sm font-medium text-white transition-all ${
          toast.type === 'error' ? 'bg-red-600' : 'bg-green-600'
        }`}>
          {toast.message}
        </div>
      )}
    </div>
  );
}

function Spinner({ small }) {
  return (
    <svg className={`${small ? 'w-4 h-4' : 'w-4 h-4'} animate-spin`} fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}
